import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root',
})
export class SweetAlertService {
  constructor(private router: Router) {}

  confirmButtonColor: string = '#4c247b';

  sweetSuccess(title: string = '', text: string, buttonText: string = 'Done') {
    Swal.fire({
      title: title,
      text: text,
      imageUrl: '/assets/images/success-check.png',
      confirmButtonColor: this.confirmButtonColor,
      confirmButtonText: buttonText,
    });
  }
  customSweetSuccess(
    title: string = 'Success',
    text: string,
    buttonText: string = 'Done',
    image: string = '/assets/images/check-green-circle.png',
  ) {
    Swal.fire({
      title: `<div style="font-size: 22px; font-weight: 600; color: #333;">${title}</div>`,
      html: `<div style="font-size: 15px; color: #555;">${text}</div>`,
      imageUrl: image,
      imageWidth: 90,
      imageHeight: 90,
      imageAlt: 'Success',
      confirmButtonText: buttonText,
      buttonsStyling: false,
      showClass: {
        popup: '',
      },
      hideClass: {
        popup: 'slideCenterToBottom',
      },
      customClass: {
        popup: 'custom-swal-popup slideTopToCenter',
        confirmButton: 'custom-swal-button w-100',
      },
      showConfirmButton: true,
      backdrop: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
      didOpen: (popup) => {
        // Ensure SweetAlert's transform doesn't override animation
        popup.style.transform = 'none';
        const btn = popup.querySelector('.custom-swal-button');
        btn?.addEventListener('click', () => Swal.close());
      },
    });
  }

  // bookedSuccessfully(title: string = '', text: string, url: string) {
  //   Swal.fire({
  //     title: title,
  //     text: text,
  //     imageUrl: '/assets/images/success-check.png',
  //     confirmButtonText: 'View Orders',
  //     confirmButtonColor: this.confirmButtonColor,
  //   }).then((result) => {
  //     if (result.isConfirmed) {
  //       this.router.navigate([url]);
  //     }
  //   });
  // }
  bookedSuccessfully(
    title: string = '',
    text: string,
    url: string,
    queryParams: any = {},
  ) {
    Swal.fire({
      title: title,
      text: text,
      imageUrl: '/assets/images/success-check.png',
      confirmButtonText: 'View Orders',
      confirmButtonColor: this.confirmButtonColor,
    }).then((result) => {
      if (result.isConfirmed) {
        this.router.navigate([url], { queryParams });
      }
    });
  }

  async disConnectedConnector(
    title: string,
    text: string,
    confirmButtonText: string,
  ) {
    let result = await Swal.fire({
      title: title,
      text: text,
      confirmButtonText: confirmButtonText,
      cancelButtonText: 'Keep it ',
      cancelButtonColor: this.confirmButtonColor,
      showCancelButton: true,
      customClass: {
        confirmButton:
          'text-danger bg-transparent border border-danger w-45 py-2 fw-bold',
        cancelButton: 'w-45 rounded-2 fw-bold',
        title: 'fs-5 text-black fw-normal mt-3',
      },
    });

    return result.isConfirmed;
  }

  /** Generic yes/no confirmation; resolves true only when the user confirms. */
  async sweetConfirm(
    title: string,
    text: string,
    confirmButtonText: string = 'Confirm',
    cancelButtonText: string = 'Cancel',
  ): Promise<boolean> {
    const result = await Swal.fire({
      title: title,
      text: text,
      icon: 'question',
      confirmButtonText: confirmButtonText,
      cancelButtonText: cancelButtonText,
      confirmButtonColor: this.confirmButtonColor,
      showCancelButton: true,
      reverseButtons: true,
      customClass: {
        cancelButton: 'w-45 rounded-2 fw-bold',
        confirmButton: 'w-45 rounded-2 fw-bold',
        title: 'fs-5 text-black fw-normal mt-3',
      },
    });

    return result.isConfirmed;
  }

  /**
   * Testo leggibile di un errore HTTP. Il BE risponde con
   * `ErrorResponse { status, messages[], statusCode }`: i `messages` hanno la
   * precedenza sul testo generico di Angular ("Http failure response for …").
   */
  private errorText(error: any): string {
    if (typeof error === 'string') return error;

    if (error?.status === 0)
      return 'Unable to reach the server. Please check your connection or try again later.';

    const messages = error?.error?.messages;
    if (Array.isArray(messages) && messages.length)
      return messages.join('<br>');

    return (
      error?.error?.message ||
      error?.error?.error ||
      error?.message ||
      'Oops! Something went wrong'
    );
  }

  /**
   * correlationId della richiesta fallita: dall'header `X-Correlation-Id`
   * della risposta (il BE lo rimanda indietro), altrimenti l'ultimo generato
   * dall'interceptor (può appartenere a una richiesta successiva).
   */
  private errorCorrelationId(error: any): string {
    return (
      error?.headers?.get?.('X-Correlation-Id') ||
      sessionStorage.getItem('correlationId') ||
      ''
    );
  }

  sweetError(title: string = '', error: any) {
    const text = this.errorText(error);
    const correlationId = this.errorCorrelationId(error);

    Swal.fire({
      title: title,
      html: `${text} <br> <b>correlationId: ${correlationId}</b>`,
      imageUrl: '/assets/images/error.png',
      confirmButtonText: 'Dismiss',
      confirmButtonColor: this.confirmButtonColor,
      showCloseButton: true,
    });
  }

  /**
   * Come {@link sweetError}, con un'azione aggiuntiva (es. "Salva come bozza"
   * quando la prenotazione fallisce): il bottone principale esegue `onClick`,
   * "Dismiss" chiude e basta. Senza `action` si comporta come sweetError.
   */
  sweetErrorWithAction(
    title: string,
    error: any,
    action?: { label: string; hint?: string; onClick: () => void },
  ) {
    if (!action) {
      this.sweetError(title, error);
      return;
    }

    const text = this.errorText(error);
    const correlationId = this.errorCorrelationId(error);
    const hint = action.hint ? `<br><br>${action.hint}` : '';

    Swal.fire({
      title: title,
      html: `${text}${hint} <br> <b>correlationId: ${correlationId}</b>`,
      imageUrl: '/assets/images/error.png',
      showCancelButton: true,
      confirmButtonText: action.label,
      cancelButtonText: 'Dismiss',
      confirmButtonColor: this.confirmButtonColor,
      reverseButtons: true,
      showCloseButton: true,
      customClass: {
        confirmButton: 'w-45 fw-bold rounded',
        cancelButton: 'swal-cancel-btn w-45 fw-bold rounded',
      },
    }).then((result) => {
      if (result.isConfirmed) action.onClick();
    });
  }

  sweetErrors(
    title: string = '',
    err: any,
    customErrors?: any,
    buttonText: string = 'Dismiss',
  ) {
    let error: [];
    if (customErrors) {
      error = customErrors;
    } else error = err?.error?.messages || [];

    const correlationId = this.errorCorrelationId(err);

    Swal.fire({
      title: 'Oops',
      html: `${
        error.toString() || 'Ops! Something went wrong'
      } <br> <b>correlationId: ${correlationId}</b>`,
      imageUrl: '/assets/images/error.png',
      confirmButtonText: buttonText,
      confirmButtonColor: this.confirmButtonColor,
      showCloseButton: true,
    });
  }

  sweetInfo(title: string = '', text: string) {
    Swal.fire({
      title: title,
      text: text,
      icon: 'info',
      confirmButtonColor: this.confirmButtonColor,
    });
  }
  showInfoHtml(title: string = '', html: string) {
    Swal.fire({
      title: title,
      html: html,
      icon: 'info',
    });
  }
  sweetWarning(
    title: string = '',
    text: string,
    confirmButtonText: string = '',
  ): Promise<boolean> {
    return Swal.fire({
      title: title || 'Are you sure?',
      text: text || "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: this.confirmButtonColor,
      cancelButtonColor: '#d33',
      confirmButtonText: confirmButtonText || 'Yes, delete it!',
    }).then((result) => {
      return result.isConfirmed;
    });
  }

  sweetOnlyWarning(title: string = '', text: string) {
    return Swal.fire({
      title: title || '',
      text: text || '',
      icon: 'warning',
    });
  }

  sweetWarnings(warnings: any) {
    const formattedHtml = warnings.map(
      (w: any) => `<strong>${w.title}:</strong> <br/> ${w.detail}`,
    );

    return Swal.fire({
      title: 'Warning',
      html: formattedHtml,
      icon: 'warning',
    });
  }

  showErrorToast(
    title: string,
    text: string,
    image: string = '/assets/images/danger.svg',
  ) {
    Swal.fire({
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 5000,

      background: '#F5093D1A',

      showCloseButton: true,
      closeButtonHtml: '✕',
      customClass: {
        popup: 'custom-error-toast',
        title: 'custom-toast-title',
        htmlContainer: 'custom-toast-text',
        image: 'custom-toast-icon',
        closeButton: 'custom-toast-close',
      },
      title: `<div class="custom-toast-title-with-icon">
              <img src="${image}" class="custom-toast-icon-img" />
              <div class="custom-toast-title">
              ${title}
               <div class="custom-toast-text mt-2">${text} </div>
              </div>
           
           </div>`,
    });
  }

  customWithActions(
    title: string,
    description: string,
    image: string = '/assets/icons/refreshing.svg',
    confirmText: string = 'Refesh',
    onConfirm?: () => void,
  ) {
    return Swal.fire({
      html: `
      <div class="swal-container">
        <img src="${image}" class="swal-image" />

        <h3 class="swal-title">${title}</h3>

        <p class="swal-description">
          ${description}
        </p>

        <div class="swal-actions">
          <button id="confirmBtn" class="swal-btn primary">
            ${confirmText}
          </button>
        </div>
      </div>
    `,
      showConfirmButton: false,
      didOpen: () => {
        const confirmBtn = document.getElementById('confirmBtn');

        confirmBtn?.addEventListener('click', () => {
          Swal.close();
          onConfirm?.();
        });
      },
    });
  }
  logoutConfirmation(): Promise<boolean> {
    return Swal.fire({
      title: 'Logout',
      text: 'Are you sure you want to logout?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Logout',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#EB001B',

      reverseButtons: true,
      customClass: {
        confirmButton: 'w-45 fw-bold rounded',
        cancelButton: 'swal-cancel-btn w-45 fw-bold rounded ',
        title: 'fs-5 text-black',
      },
    }).then((result) => result.isConfirmed);
  }
}
